import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";

import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

import MDButton from "components/MDButton";
import { slugify } from "components/content/contentUtils";
import { listCategories, createCategory, updateCategory } from "api/endpoints/categories";

/**
 * <CategoryForm> — create / edit a website content category. Categories may
 * nest (a parent) and carry a sort order. Used by the Categories management
 * screen inside a SidePanel.
 */
function CategoryForm({ websiteId, category, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(category?.content_category_id);

  const [form, setForm] = useState({
    name: category?.name || "",
    slug: category?.slug || "",
    description: category?.description || "",
    parent_id: category?.parent_id || "",
    sort_order: category?.sort_order != null ? String(category.sort_order) : "",
  });
  const [slugTouched, setSlugTouched] = useState(Boolean(category?.slug));
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  // Parent options — every category except the one being edited.
  useEffect(() => {
    if (!websiteId) return;
    listCategories(websiteId)
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [websiteId]);

  const handleName = (event) => {
    const name = event.target.value;
    setForm((prev) => ({ ...prev, name, slug: slugTouched ? prev.slug : slugify(name) }));
  };

  const handleSlug = (event) => {
    setSlugTouched(true);
    setForm((prev) => ({ ...prev, slug: slugify(event.target.value) }));
  };

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      enqueueSnackbar("Name is required", { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), slug: form.slug || slugify(form.name) };
      if (form.description) payload.description = form.description;
      if (form.parent_id) payload.parent_id = form.parent_id;
      const order = parseInt(form.sort_order, 10);
      if (!Number.isNaN(order)) payload.sort_order = order;

      const result = isEdit
        ? await updateCategory(websiteId, category.content_category_id, payload)
        : await createCategory(websiteId, payload);
      enqueueSnackbar(isEdit ? "Category updated" : "Category created", { variant: "success" });
      onSaved(result);
    } catch {
      enqueueSnackbar("Failed to save category", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const parentOptions = categories.filter(
    (c) => c.content_category_id !== category?.content_category_id
  );

  return (
    <Stack component="form" spacing={3} onSubmit={handleSubmit} noValidate>
      <TextField label="Name" value={form.name} onChange={handleName} fullWidth required />
      <TextField
        label="Slug"
        value={form.slug}
        onChange={handleSlug}
        fullWidth
        helperText="Used in content URLs and filters"
      />
      <TextField
        label="Description"
        value={form.description}
        onChange={set("description")}
        fullWidth
        multiline
        minRows={2}
      />
      <TextField
        select
        label="Parent category"
        value={form.parent_id}
        onChange={set("parent_id")}
        fullWidth
      >
        <MenuItem value="">None (top level)</MenuItem>
        {parentOptions.map((c) => (
          <MenuItem key={c.content_category_id} value={c.content_category_id}>
            {c.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Sort order"
        type="number"
        value={form.sort_order}
        onChange={set("sort_order")}
        fullWidth
        helperText="Lower numbers appear first"
      />
      <MDButton type="submit" color="info" variant="gradient" disabled={saving} fullWidth>
        {isEdit ? "Update Category" : "Create Category"}
      </MDButton>
    </Stack>
  );
}

CategoryForm.defaultProps = {
  websiteId: undefined,
  category: null,
};

CategoryForm.propTypes = {
  websiteId: PropTypes.string,
  category: PropTypes.shape({
    content_category_id: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    description: PropTypes.string,
    parent_id: PropTypes.string,
    sort_order: PropTypes.number,
  }),
  onSaved: PropTypes.func.isRequired,
};

export default CategoryForm;
